import axios from 'axios'
import { ref } from 'vue'
import { useToast } from 'vue-toast-notification'

export function useClientRequests({
    onCreated = () => {},
    onUpdated = () => {},
    onFetched = () => {},
    onDeleted = () => {},
    onDuplicated = () => {},
    onStatusChanged = () => {},
    onReasonForLossUpdated = () => {},
    onError = () => {},
}) {
    const $toast = useToast()

    const creating = ref(false)
    const updating = ref(false)
    const fetching = ref(false)
    const deleting = ref(false)
    const duplicating = ref(false)
    const changingStatus = ref(false)
    const updatingReasonForLoss = ref(false)
    const errors = ref(null)
    const clientRequest = ref(null)

    const buildClientRequestFormData = (form) => {
        const formData = new FormData()

        Object.entries(form).forEach(([key, value]) => {
            if (key === 'tags') {
                return value.forEach((tag) => {
                    formData.append(`${key}[]`, tag.id)
                })
            }
            if (typeof value === 'boolean') {
                return formData.append(key, value ? 1 : 0)
            }
            if (value !== null && value !== '') {
                formData.append(key, value)
            }
        })

        return formData
    }

    const createClientRequest = async (form) => {
        try {
            creating.value = true
            const formData = buildClientRequestFormData(form)
            const response = await axios.post('/api/client-requests', formData)

            $toast.success('Demande client créée avec succès', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            })
            onCreated(response.data.data)
        } catch (error) {
            errors.value = error.response?.data?.errors
            onError(error)
        } finally {
            creating.value = false
        }
    }

    const updateClientRequest = async (id, form) => {
        try {
            updating.value = true
            const formData = buildClientRequestFormData(form)
            formData.append('_method', 'PUT')
            await axios.post(`/api/client-requests/${id}`, formData)

            $toast.success('Demande client modifiée avec succès', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            })
            onUpdated()
        } catch (error) {
            errors.value = error.response?.data?.errors
            onError(error)
        } finally {
            updating.value = false
        }
    }

    const fetchClientRequest = async (id) => {
        if (!id) {
            console.warn('ClientRequest ID is required.')
            return
        }
        try {
            fetching.value = true
            const response = await axios.get(`/api/client-requests/${id}`)
            clientRequest.value = response.data
            onFetched(response.data.data)
        } catch (error) {
            onError(error)
        } finally {
            fetching.value = false
        }
    }

    const deleteClientRequest = async (id) => {
        try {
            deleting.value = true
            await axios.delete(`/api/client-requests/${id}`)
            onDeleted()
        } catch (error) {
            onError(error)
        } finally {
            deleting.value = false
        }
    }

    const duplicateClientRequest = async (id) => {
        try {
            duplicating.value = true
            const response = await axios.post(`/api/client-requests/${id}/duplicate`)
            onDuplicated(response.data.data)
        } catch (error) {
            onError(error)
        } finally {
            duplicating.value = false
        }
    }

    const changeStatus = async (id, status) => {
        try {
            changingStatus.value = true
            await axios.patch(`/api/client-requests/${id}/status`, { status })
            $toast.success('Statut mis à jour', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            })
            onStatusChanged(id, status)
        } catch (error) {
            onError(error)
        } finally {
            changingStatus.value = false
        }
    }

    const updateReasonForLoss = async (id, reasonForLoss) => {
        try {
            updatingReasonForLoss.value = true
            const response = await axios.patch(`/api/client-requests/${id}/reason-for-loss`, {
                reason_for_loss: reasonForLoss
            })
            $toast.success('Statut et raison de la perte mise à jour', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            })
            onReasonForLossUpdated(id, response.data.data)
        } catch (error) {
            onError(error)
        } finally {
            updatingReasonForLoss.value = false
        }
    }

    return {
        creating,
        updating,
        fetching,
        deleting,
        duplicating,
        changingStatus,
        updatingReasonForLoss,
        errors,
        clientRequest,
        createClientRequest,
        updateClientRequest,
        fetchClientRequest,
        deleteClientRequest,
        duplicateClientRequest,
        changeStatus,
        updateReasonForLoss,
    }
}
